import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as CustomerAPI from 'api/Customer';
import { dispatch } from 'store';
import { TYPE_OF_FIELD_IN_COLUMN } from 'utils/scorecard';
import { openSnackbar } from 'store/reducers/snackbar';

const displayMessage = ({ type, msg }) => {
  dispatch(
    openSnackbar({
      open: true,
      message: msg ? msg : type === 'success' ? 'Success' : 'Something went wrong',
      variant: 'alert',
      alert: {
        color: type || 'success'
      },
      close: false
    })
  );
};

const initialState = {
  pagination: {
    pageSize: 10,
    currentPage: 1,
    totalRecords: null
  },
  column: [
    { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
    { title: 'Customer Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.USER, width: 210 },
    { title: 'Contact', key: 'phone_number', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
    { title: 'Status', key: 'status', field_type: TYPE_OF_FIELD_IN_COLUMN.STATUS, width: 120 },
    { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
  ],
  data: null,
  customerData: null,
  contacts: null,
  status: '',
  ui: {
    loading: false,
    msg: ''
  }
};

const getCustomersData = createAsyncThunk('customer/getCustomersData', async ({ params, signal }) => {
  const res = await CustomerAPI.getCustomersData(params, signal);
  return res?.data?.data;
});

const getCustomerDataById = createAsyncThunk('customer/getCustomerDataById', async ({ params, signal }) => {
  const res = await CustomerAPI.getCustomerDataById(params, signal);
  return res?.data?.data;
});

const createCustomer = createAsyncThunk('customer/createCustomer', async (params) => {
  try {
    const res = await CustomerAPI.createCustomer(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateCustomer = createAsyncThunk('customer/updateTemplatePoint', async (params) => {
  try {
    const res = await CustomerAPI.updateCustomer(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteCustomer = createAsyncThunk('customer/deleteCustomer', async (id) => {
  try {
    const res = await CustomerAPI.deleteCustomer(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const addTenant = createAsyncThunk('customer/addTenant', async (params) => {
  try {
    const res = await CustomerAPI.addTenant(params);
    displayMessage({ type: 'success', msg: 'Tenant added successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateTenant = createAsyncThunk('customer/updateTenant', async (params) => {
  try {
    const res = await CustomerAPI.updateTenant(params);
    displayMessage({ type: 'success', msg: 'Tenant updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const getContactsByCustomerId = createAsyncThunk('customer/getContactsByCustomerId', async ({ params, signal }) => {
  const res = await CustomerAPI.getContactsByCustomerId(params, signal);
  return res?.data?.data;
});

const createContact = createAsyncThunk('customer/createContact', async (params) => {
  try {
    const res = await CustomerAPI.createContact(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateContact = createAsyncThunk('customer/updateContact', async (params) => {
  try {
    const res = await CustomerAPI.updateContact(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteContact = createAsyncThunk('customer/deleteContact', async (id) => {
  try {
    const res = await CustomerAPI.deleteContact(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const CustomerSlice = createSlice({
  name: 'customer',
  initialState: initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
    }
  },
  extraReducers: {
    [getCustomersData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getCustomersData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.pagination.totalRecords = action.payload?.count;
      state.data = action.payload?.results || [];
    },
    [getCustomersData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [getCustomerDataById.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getCustomerDataById.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.customerData = action.payload;
    },
    [getCustomerDataById.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createCustomer.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createCustomer.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createCustomer.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateCustomer.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateCustomer.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [updateCustomer.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteCustomer.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteCustomer.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteCustomer.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [addTenant.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [addTenant.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [addTenant.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateTenant.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateTenant.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [updateTenant.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getContactsByCustomerId.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getContactsByCustomerId.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.contacts = action.payload;
    },
    [getContactsByCustomerId.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createContact.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createContact.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createContact.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateContact.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateContact.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [updateContact.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteContact.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteContact.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteContact.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    }
  }
});

const { resetState, setCurrentPage, setPageSize } = CustomerSlice.actions;

export default CustomerSlice.reducer;

export {
  resetState,
  setCurrentPage,
  setPageSize,
  getCustomersData,
  getCustomerDataById,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  addTenant,
  updateTenant,
  getContactsByCustomerId,
  createContact,
  updateContact,
  deleteContact
};
